import { isExpresionAvatar } from '@alter/types';
import { EXPRESIONES_CONFIG } from './expresiones.js';
import { DEFAULT_TRANSITION_MS, NEUTRAL_PARAMS, type RigParams } from './types.js';

/**
 * Adapter del runtime gráfico del rig. Implementaciones concretas
 * (`ThreeRigAdapter` en el MVP) viven en las apps, NUNCA en este
 * paquete (`docs/04_ARQUITECTURA.md` §5.3, §6.4).
 */
export interface RigAdapter {
  /**
   * Aplica la pose objetivo interpolando desde la pose actual durante
   * `durationMs` ms. La interpolación es responsabilidad del adapter:
   * escribir pesos de blend shapes no tiene tween nativo, así que el
   * adapter lo hace con `requestAnimationFrame` o equivalente (el de
   * three.js se apoya en el render loop de `@react-three/fiber`).
   */
  transitionTo(params: RigParams, durationMs: number): void;

  /** Lee la pose actual del rig. Útil para debug y cancelación de transiciones. */
  getCurrentParams(): RigParams;
}

/** Opciones de `ExpressionEngine.apply()`. */
export interface ExpressionApplyOpts {
  /**
   * Override de la intensidad final. Tiene prioridad sobre
   * `EXPRESIONES_CONFIG[expresion].intensidad`.
   *
   * **El wire vigente no trae intensidad** para este cliente (JC §3.1):
   * en una sesión, cada expresión usa la de su config. El override existe
   * para las páginas de desarrollo y para la `intensidad` por evento de
   * `expresion_detalle`, cuando llegue (ítem 61 del core).
   */
  intensidad?: number;
  /**
   * Duración de la transición entre la pose actual y la nueva, en ms.
   * Default `DEFAULT_TRANSITION_MS` (800). No es la duración del audio
   * del paciente (`duracion_audio_segundos` del envelope).
   */
  durationMs?: number;
}

/**
 * Resuelve un valor de `expresion_avatar` a la pose `RigParams`
 * completa, sin tocar ningún adapter.
 *
 * 1. Si la expresión no está en `EXPRESIONES_AVATAR` → fallback
 *    `NEUTRAL` (P3): un valor desconocido del backend nunca rompe.
 * 2. `{ ...NEUTRAL_PARAMS, ...EXPRESIONES_CONFIG[key] }` — los campos
 *    ausentes de la config caen al neutro.
 * 3. `intensidad = opts?.intensidad ?? config.intensidad ?? 1.0`.
 *
 * Es pura y está expuesta aparte de `ExpressionEngine` porque hay
 * consumidores que necesitan la pose sin aplicarla — la página de
 * calibración de F0.6 la usa para poblar sus sliders. Antes esto vivía
 * dentro de `apply()`; extraerlo evita que esos consumidores tengan que
 * reimplementar la resolución y se les desincronice.
 */
export function resolveExpresion(expresion_avatar: string, opts?: ExpressionApplyOpts): RigParams {
  const key = isExpresionAvatar(expresion_avatar) ? expresion_avatar : 'NEUTRAL';
  const config = EXPRESIONES_CONFIG[key];
  return {
    ...NEUTRAL_PARAMS,
    ...config,
    intensidad: opts?.intensidad ?? config.intensidad ?? NEUTRAL_PARAMS.intensidad,
  };
}

/**
 * Motor paramétrico renderer-agnostic. Resuelve un valor de
 * `expresion_avatar` a `RigParams` (ver `resolveExpresion`) y delega al
 * `RigAdapter`.
 */
export class ExpressionEngine {
  constructor(private readonly adapter: RigAdapter) {}

  apply(expresion_avatar: string, opts?: ExpressionApplyOpts): void {
    this.adapter.transitionTo(
      resolveExpresion(expresion_avatar, opts),
      opts?.durationMs ?? DEFAULT_TRANSITION_MS,
    );
  }
}
